import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  InputLabel,
  Stack,
} from '@mui/material'
import { Controller, FieldValues } from 'react-hook-form'
import type { UseFormTrigger } from 'react-hook-form'
import { DatePicker } from 'shared/components'
import {
  DateFieldClearButtonCell,
  DateFieldInputRow,
  LabeledDateFieldGroup,
} from '../ScheduleFields.style'
import {
  fieldCaptionLabelSx,
  type RepeatUntilDatePickerSharedProps,
} from '../ScheduleFields.utils'
import {
  ScheduleTimePickers,
  type ScheduleTimePickersProps,
} from '../ScheduleTimePickers'

export type ScheduleOptionsProps<T extends FieldValues> =
  ScheduleTimePickersProps<T> & {
    shouldShowTimeFields: boolean
    scheduleAllDayCheckboxUsesSetValue: boolean
    setScheduleAllDay: (checked: boolean) => void
    onScheduleAllDayChange: (options: {
      checked: boolean
      wasAllDay: boolean
    }) => void
    repeatUntilPickerShared: RepeatUntilDatePickerSharedProps
    repeatUntilRevalidate?: () => Promise<void>
    shouldValidateInterdependentFields: boolean
    trigger: UseFormTrigger<T>
    onClearRepeatUntil: () => void
    clearLabel: string
  }

export function ScheduleOptions<T extends FieldValues>(
  props: ScheduleOptionsProps<T>
) {
  const {
    shouldShowTimeFields,
    scheduleAllDayCheckboxUsesSetValue,
    setScheduleAllDay,
    onScheduleAllDayChange,
    repeatUntilPickerShared,
    repeatUntilRevalidate,
    shouldValidateInterdependentFields,
    trigger,
    onClearRepeatUntil,
    clearLabel,
    ...timePickerProps
  } = props

  const {
    control,
    fieldNames,
    valueConfig,
    frequency,
    disabled,
    t,
    labels,
    getFieldError,
  } = timePickerProps

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      {shouldShowTimeFields && frequency !== valueConfig.freqDaily ? (
        <>
          <Controller
            name={fieldNames.scheduleAllDay as any}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    disabled={disabled}
                    onChange={(_, checked) => {
                      const wasAllDay = field.value
                      if (scheduleAllDayCheckboxUsesSetValue) {
                        setScheduleAllDay(checked)
                      } else {
                        field.onChange(checked)
                      }
                      onScheduleAllDayChange({ checked, wasAllDay })
                    }}
                  />
                }
                label={t('accessSchedule.allDay')}
              />
            )}
          />
        </>
      ) : null}
      <ScheduleTimePickers
        {...timePickerProps}
        shouldShow={shouldShowTimeFields}
      />
      <LabeledDateFieldGroup>
        <InputLabel
          shrink
          htmlFor={String(fieldNames.repeatUntil)}
          sx={fieldCaptionLabelSx}
        >
          {labels.repeatUntil}
        </InputLabel>
        <DateFieldInputRow container>
          <Controller
            name={fieldNames.repeatUntil as any}
            control={control}
            render={({ field }) => (
              <>
                <Grid item xs={12} md={9}>
                  <DatePicker
                    {...field}
                    disabled={disabled}
                    externalLabel
                    inputId={String(fieldNames.repeatUntil)}
                    {...repeatUntilPickerShared}
                    revalidateFunc={repeatUntilRevalidate ?? (async () => {})}
                    errorMessage={getFieldError(fieldNames.repeatUntil)}
                    onChange={(value: Date | null) => {
                      field.onChange(value)
                      if (shouldValidateInterdependentFields) {
                        void trigger([
                          fieldNames.repeatUntil,
                          fieldNames.scheduleFromDate,
                          fieldNames.scheduleToDate,
                        ] as any)
                      }
                    }}
                  />
                </Grid>
                <DateFieldClearButtonCell item xs={12} md={2}>
                  <Button
                    disabled={disabled || field.value == null}
                    fullWidth={false}
                    onClick={onClearRepeatUntil}
                  >
                    {clearLabel}
                  </Button>
                </DateFieldClearButtonCell>
              </>
            )}
          />
        </DateFieldInputRow>
      </LabeledDateFieldGroup>
    </Stack>
  )
}
